/**
 * Site header.
 *
 * Owns exactly one thing: the scrolled state. Everything about how the header
 * looks and where it sits is CSS (see the "Header state machine" block in
 * assets/critical.css), so it is correct on first paint before any script runs.
 *
 * WHY THIS IS A REAL ASSET FILE AND NOT A {% javascript %} BLOCK
 * (CLAUDE.md prefers the inline form for section-scoped behaviour):
 * the header is global, and section JS is bundled and injected by Shopify in an
 * order we cannot see, control, or test locally. That already cost one silent
 * failure — the component upgraded before assets/scroll.js had run and never
 * subscribed. As an explicit `<script defer>` in layout/theme.liquid it loads on
 * every page, provably after scroll.js, and shows up in the network panel when
 * something goes wrong.
 *
 * LOAD ORDER: after lenis.min.js and scroll.js. All three are `defer`, which
 * preserves document order. The ready-event handling below means it is still
 * correct if that ever stops being true.
 */
if (!customElements.get('site-header')) {
  customElements.define(
    'site-header',
    class SiteHeader extends HTMLElement {
      connectedCallback() {
        /**
         * READ THE STATE OUT OF THE DOM, DO NOT ASSUME IT.
         *
         * This flag and the `data-scrolled` attribute are two copies of one
         * fact, and update() only acts when they agree with its expectations:
         * the "switch on" branch requires the flag false, the "switch off"
         * branch requires it true. Hardcoding `false` here while the element
         * still carries the attribute — a theme-editor re-render, or the node
         * being moved in the DOM — leaves neither branch reachable, and the
         * header keeps a scrolled ground it can no longer release.
         */
        this.scrolled = this.hasAttribute('data-scrolled');
        this.bound = false;
        this.nativeBound = false;

        /**
         * Real values before the first measure(), because measure() can decline
         * to run (see its scroll-lock guard) and update() compares against these
         * numerically — `scrollY > undefined` is false, which would leave the
         * header inert rather than merely unmeasured. These are the same values
         * measure() settles on for any page that scrolls at all.
         */
        this.thresholdOn = 2;
        this.thresholdOff = 1;
        this.scrollable = true;

        this.handleScroll = this.handleScroll.bind(this);
        this.handleNativeScroll = this.handleNativeScroll.bind(this);
        this.bindScroll = this.bindScroll.bind(this);
        this.measure = this.measure.bind(this);

        this.markRoot();
        this.measure();

        /**
         * A bfcache restore brings the page back at its previous scroll offset
         * with no scroll event — the back button after a form POST is the common
         * way in. Without this the header keeps whatever state it was frozen
         * with. `measure()` re-reads both the page height and the real scroll
         * position, so it is the right thing to run here.
         */
        window.addEventListener('pageshow', this.measure);

        /**
         * Recompute on resize AND when the document's height changes — a lazy
         * image landing or a section rendering in the theme editor both move the
         * threshold. Observing documentElement rather than polling keeps
         * scrollHeight out of the scroll handler, where it would force a
         * synchronous layout on every event.
         */
        this.documentObserver = new ResizeObserver(this.measure);
        this.documentObserver.observe(document.documentElement);
        window.addEventListener('resize', this.measure, { passive: true });

        if (window.themeScroll) {
          this.bindScroll();
        } else {
          document.addEventListener('themescroll:ready', this.bindScroll, { once: true });

          /**
           * Provisional native listener, dropped the moment the controller takes
           * over. Without it, anything that stops scroll.js running — a failed
           * asset, a Lenis error — leaves the header with no scroll source and no
           * scrolled state, silently.
           */
          window.addEventListener('scroll', this.handleNativeScroll, { passive: true });
          this.nativeBound = true;
          this.update(this.currentScroll());
        }

        /**
         * Published for the mobile menu and mega menu, which need to open flush
         * beneath the header. Nothing in this file consumes it.
         */
        this.sizeObserver = new ResizeObserver(() => this.publishHeight());
        this.sizeObserver.observe(this);
        this.publishHeight();

        /**
         * Re-colour the browser chrome whenever any state on this element
         * changes. mobile-menu.js, search-overlay.js and cart-drawer.js already
         * set these attributes, so none of them needs to know this exists.
         */
        this.syncChromeTint = this.syncChromeTint.bind(this);
        this.tintObserver = new MutationObserver(this.syncChromeTint);
        this.tintObserver.observe(this, {
          attributes: true,
          attributeFilter: [
            'data-scrolled',
            'data-menu-open',
            'data-search-open',
            'data-cart-open',
          ],
        });
        this.syncChromeTint();
      }

      disconnectedCallback() {
        document.removeEventListener('themescroll:ready', this.bindScroll);
        window.removeEventListener('resize', this.measure);
        window.removeEventListener('pageshow', this.measure);
        this.unbindNative();

        if (this.bound) window.themeScroll.off('scroll', this.handleScroll);
        if (this.documentObserver) this.documentObserver.disconnect();
        if (this.sizeObserver) this.sizeObserver.disconnect();
        if (this.tintObserver) this.tintObserver.disconnect();
        if (this.resampleFrame) cancelAnimationFrame(this.resampleFrame);

        /**
         * Hand the chrome tint back to the CSS fallbacks in critical.css rather
         * than leaving the last resolved state frozen on :root. A theme-editor
         * re-render is the way in, and the strips outlive this element — they
         * live in layout/theme.liquid, not here.
         */
        document.documentElement.style.removeProperty('background-color');
        document.documentElement.style.removeProperty('--chrome-tint-top');
        document.documentElement.style.removeProperty('--chrome-tint-bottom');
        if (this.root) this.root.removeAttribute('data-header-root');

        document.documentElement.style.removeProperty('--header-height');
      }

      bindScroll() {
        // The ready event can land after the theme editor has removed us.
        if (!this.isConnected) return;

        this.unbindNative();
        window.themeScroll.on('scroll', this.handleScroll);
        this.bound = true;
        this.update(this.currentScroll());
      }

      /**
       * The payload's `scroll` is deliberately ignored — see currentScroll().
       * The event is the trigger; the document is the source of truth.
       */
      handleScroll() {
        this.update(this.currentScroll());
      }

      handleNativeScroll() {
        this.update(this.currentScroll());
      }

      unbindNative() {
        if (!this.nativeBound) return;
        window.removeEventListener('scroll', this.handleNativeScroll);
        this.nativeBound = false;
      }

      /**
       * Work out where the scrolled state should switch on.
       *
       * ESSENTIALLY IMMEDIATELY — 2px. This is not a stylistic choice.
       *
       * The header is sticky and transparent at rest, so content passes behind it
       * from the FIRST PIXEL of scroll: at scroll S, the content that began at the
       * header's bottom edge sits S pixels up inside the header band. Measured at
       * S=1 -> 1px behind, S=40 -> 40px, S=56 -> fully covered.
       *
       * Two earlier values were both wrong for the same reason — they assumed
       * content only reaches the header after some distance:
       *   50% of viewport   left ~400px of scrolling with content visible through
       *                     a transparent bar
       *   the header height left ~102px of it on mobile, which is what was
       *                     reported on iOS Safari
       *
       * There is nothing behind the header at S=0, so transparent at rest is still
       * correct; the background just has to arrive the moment scrolling starts.
       * The 2px is only to avoid firing on iOS rubber-band jitter at the top.
       *
       * Still capped at half the scrollable distance so a page that barely scrolls
       * cannot latch the state on.
       *
       * THE RELEASE POINT MUST BE ABOVE ZERO, and this is the whole reason it is 1
       * and not 0. The test below is `scrollY < thresholdOff`, so a release point of
       * 0 asks for a NEGATIVE scroll position — unreachable, which latched the state
       * on permanently: back at the top of the page the bar kept its ground, its
       * scrolled logo and its drop shadow. At 1 it releases at rest (Lenis reports a
       * fractional position, so this must be a `<` against 1, not an `=== 0`).
       *
       * The 1px gap between on (>2) and off (<1) is the hysteresis: a header parked
       * exactly on the boundary cannot strobe between two colour schemes.
       */
      measure() {
        /**
         * NOT WHILE SCROLL IS LOCKED. Locking adds `overflow-hidden` to <html>,
         * which changes the root's box and therefore fires the ResizeObserver
         * that calls this method. The page's scrollable distance is not
         * meaningful in that state, and reading it as zero would take
         * `scrollable` false and drop `data-scrolled` — so opening the cart, the
         * mobile menu, search or the enquiry popup would strip the header's
         * ground out from under the panel. The unlock reflows the root again and
         * brings us straight back here with real numbers.
         */
        if (window.themeScroll && window.themeScroll.isLocked) return;

        var scrollable = Math.max(
          0,
          document.documentElement.scrollHeight - window.innerHeight
        );

        this.thresholdOn = Math.min(2, scrollable * 0.5);
        this.thresholdOff = 1;

        // A page that cannot scroll has no scrolled state.
        this.scrollable = scrollable > 0;

        this.update(this.currentScroll());
      }

      /**
       * ALWAYS `window.scrollY`, NEVER `themeScroll.scrollY`, AND THE DIFFERENCE
       * MATTERS HERE.
       *
       * themeScroll.scrollY reports Lenis's ANIMATED position when Lenis is
       * running. That is the right value for anything being animated in step
       * with the page, and the wrong one for a threshold test: it can sit some
       * distance from the document's real offset after a fragment jump (a form
       * POST returning to `#ContactForm-…` or `#EnquiryPopup-…`) or across a
       * `stop()`/`start()` cycle from a scroll lock. A 2px trigger has no
       * tolerance for that — a small persistent offset either latches the
       * scrolled state on at the top of the page or never lets it arrive.
       *
       * The Lenis subscription is still what TELLS us to re-check; it just no
       * longer supplies the number. `window.scrollY` is the document's own
       * offset and cannot disagree with what the visitor is looking at.
       */
      currentScroll() {
        return window.scrollY;
      }

      update(scrollY) {
        if (typeof scrollY !== 'number') return;

        if (!this.scrolled && this.scrollable && scrollY > this.thresholdOn) {
          this.scrolled = true;
          this.setAttribute('data-scrolled', '');
        } else if (this.scrolled && (!this.scrollable || scrollY < this.thresholdOff)) {
          this.scrolled = false;
          this.removeAttribute('data-scrolled');
        }
      }

      /**
       * Colour the browser's own chrome — the strips above and below the page in
       * mobile Safari — to match whatever surface is currently on screen.
       *
       * THE MECHANISM IS DOCUMENTED IN ONE PLACE: the "Browser chrome tint"
       * block in assets/critical.css. Read it before changing this method. The
       * short version is that Safari 26 IGNORES `theme-color` and samples the
       * background-color of a fixed/sticky element near each viewport edge, so
       * layout/theme.liquid renders two 5px strips for it to find and this method
       * colours them.
       *
       * THOSE STRIPS ARE VISIBLE AND ON TOP — they have to be, because Safari
       * refuses to sample a hidden one, which was measured with `opacity: 0` and
       * again with `z-index: -1`. So a strip's colour is a CLAIM THAT THE SURFACE
       * UNDER IT IS ALREADY THAT COLOUR, and `transparent` is the correct answer
       * whenever no such claim can be made. This method is mostly about knowing
       * which of those two situations it is in.
       *
       * THREE OUTPUTS, ONE RESOLVER, AND THAT IS THE POINT. The theme used to
       * carry two mechanisms that could disagree with each other — a constant
       * `theme-color` meta and a paint on <html> — and they did:
       *
       *   --chrome-tint-top / --chrome-tint-bottom   Safari 26+, via the strips
       *   <html> background-color                     the rubber-band overscroll
       *                                               area, which is the ONLY
       *                                               thing this ever did; <html>
       *                                               is not read for the bars
       *   the `theme-color` meta                      iOS <= 18 and Android
       *                                               Chrome, where it still works
       *
       * TOP AND BOTTOM RESOLVE INDEPENDENTLY, because Safari resolves them
       * independently. The three panels are full-viewport on mobile, so while one
       * is open it legitimately owns both edges; every other state does not — a
       * scrolled page has the header's ground at the top and the footer's at the
       * bottom, and getting that wrong is half the reported bug.
       *
       * Panel precedence is unchanged; the panels are mutually exclusive in
       * practice, so it only decides a transient overlap.
       */
      syncChromeTint() {
        var styles = getComputedStyle(this);
        var read = function (name) {
          return styles.getPropertyValue(name).trim();
        };

        var root = document.documentElement;
        var rootStyles = getComputedStyle(root);
        var readRoot = function (name) {
          return rootStyles.getPropertyValue(name).trim();
        };

        /**
         * The page's own ground, and the last resort for both edges — including
         * for a panel whose section never published a colour, which must look
         * unchanged rather than blanking the bars.
         */
        var ground = getComputedStyle(document.body).backgroundColor;

        /**
         * A panel covers the whole viewport on mobile, so it takes both edges.
         */
        var panel = '';
        if (this.hasAttribute('data-cart-open')) panel = read('--header-bg-cart');
        else if (this.hasAttribute('data-search-open')) panel = read('--header-bg-search');
        else if (this.hasAttribute('data-menu-open')) panel = read('--header-bg-menu');

        /**
         * THE STRIP COLOURS ARE NOT "WHAT SHOULD THE BAR BE". They are "what is
         * ALREADY on screen directly under this strip" — because the strip is
         * visible, on top, and 5px tall, so any colour that is not already there
         * shows as a band. Safari will not sample a strip that is hidden; that
         * was measured twice. See the "Browser chrome tint" block in
         * assets/critical.css before changing any of this.
         *
         * TOP: --header-bg-current is exactly the right value and already
         * resolves every case, because the strip sits on the header. It is
         * TRANSPARENT at rest by design — which is correct here, not a gap: with
         * an overlaying hero there is no flat colour behind the strip to match,
         * so it stays out of the way and Safari falls back to the root colour.
         * When scrolled it is the header's ground, and the strip vanishes into
         * the bar. That is the reported bug, fixed.
         */
        var top = panel;
        if (!top) top = read('--header-bg-current');

        /**
         * BOTTOM: only a panel, and deliberately nothing else. Mid-page the
         * surface under this strip is whatever section happens to be passing,
         * which this cannot know — publishing the footer's colour there would lay
         * a band of the wrong colour over an unrelated section. Transparent means
         * Safari keeps doing what it does today at the bottom, which is not what
         * was reported.
         */
        var bottom = panel || 'transparent';

        root.style.setProperty('--chrome-tint-top', top || 'transparent');
        root.style.setProperty('--chrome-tint-bottom', bottom);

        /**
         * THE CANVAS IS A SEPARATE QUESTION AND KEEPS THE OLD ANSWER. It is
         * never transparent, because it is the fallback Safari uses at
         * scrollY = 0 — when the strip above is deliberately transparent — and
         * because it is what the rubber-band overscroll area shows. So it still
         * resolves through the rest publishers to a real colour.
         *
         * Setting a background on <html> stops <body>'s propagating to the
         * canvas. It touches no layout: <body> keeps its own background and still
         * covers the viewport.
         *
         * KNOWN LIMIT, ACCEPTED: an overlay hero contributes its colour SCHEME's
         * background here, not a colour sampled out of its photograph.
         */
        var canvas = panel;
        if (!canvas && this.hasAttribute('data-scrolled')) canvas = read('--header-bg-current');
        if (!canvas) canvas = readRoot('--chrome-tint-rest-top');
        if (!canvas) canvas = ground;
        if (!canvas) return;

        root.style.backgroundColor = canvas;

        this.publishThemeColor(canvas);
        this.forceResample();
      }

      /**
       * `theme-color`, for iOS <= 18 and Android Chrome, where it is still the
       * only lever. Safari 26 ignores it entirely — it is kept for the older
       * devices, not as a second opinion for the current one.
       *
       * ONE VALUE FOR BOTH BARS is all the tag can express, so it follows the
       * TOP. Where the two edges genuinely differ — at rest, hero above and
       * footer below — the older device gets the top colour at both ends.
       *
       * The meta is rendered server-side by snippets/meta-tags.liquid so it is
       * already right at first paint; this only tracks it through state changes.
       */
      publishThemeColor(colour) {
        var meta = document.querySelector('meta[name="theme-color"]');
        if (!meta || !colour) return;
        meta.setAttribute('content', colour);
      }

      /**
       * NUDGE SAFARI INTO RE-SAMPLING.
       *
       * The two sources on this behaviour disagree, and neither can be settled
       * from here: one reports that Safari re-evaluates whenever a fixed element
       * appears, the other that sampling happens at first paint and post-paint
       * style changes are ignored. If the second is right, changing a custom
       * property would never repaint the bars and opening the cart would do
       * nothing — which is the reported bug, unfixed.
       *
       * So do not pick a side: take the strips out of the render tree and put
       * them back, which is a structural change under either reading. It costs
       * nothing because they are invisible, and one frame's delay because a
       * synchronous toggle can be coalesced away before it is ever observed.
       */
      forceResample() {
        if (this.resampleFrame) cancelAnimationFrame(this.resampleFrame);

        this.resampleFrame = requestAnimationFrame(function () {
          var strips = document.querySelectorAll('[data-chrome-tint]');

          for (var i = 0; i < strips.length; i++) {
            strips[i].style.display = 'none';
            // Read back, so the removal cannot be collapsed into the restore.
            void strips[i].offsetHeight;
            strips[i].style.display = '';
          }
        });
      }

      /**
       * Tag this header's body-level ancestor so critical.css can position it.
       *
       * critical.css prefers a structural selector, because that is correct on
       * first paint with no JS. But that selector has to match a wrapper Shopify
       * generates, whose exact markup is not documented and could not be verified
       * here. This gives the same rules a second, exact hook. Both target the
       * same element in the normal case.
       */
      markRoot() {
        var node = this;
        while (node.parentElement && node.parentElement !== document.body) {
          node = node.parentElement;
        }

        if (node.parentElement === document.body) {
          this.root = node;
          node.setAttribute('data-header-root', '');
        }
      }

      publishHeight() {
        document.documentElement.style.setProperty(
          '--header-height',
          this.offsetHeight + 'px'
        );
      }
    }
  );
}
